import {
  makeObservable,
  observable,
  action,
  override,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
  computed,
} from "mobx";
import Dexie, { liveQuery, Subscription } from "dexie";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { OptimisticSyncUpdate, SyncModelKind, SyncUpdate } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import type {
  SessionModelData,
  SessionStatus,
  SessionKind,
  SessionConfig,
  SessionPreviousChats,
  SessionUpsertedSyncUpdateValue,
  SessionIndexTuple,
  SessionConfigVariant,
} from "@/store/sessions/types";
import { SessionObservable } from "./SessionObservable";
import { SyncUpdateValue } from "@/store/sync/types";
import { Maybe } from "@/domains/common/types";
import { uuidModule } from "@/modules/uuid";
import { CreateSessionOperation } from "@/store/sync/operations/sessions/CreateSessionOperation";
import { Uuid } from "@/domains/global/identifiers";
import { UpdateSessionOperation } from "@/store/sync/operations/sessions/UpdateSessionOperation";
import { AppStore } from "@/store/AppStore";
import { SessionIndexes } from "@/store/sessions/SessionIndexes";
import { searchPreviousChatSessions } from "@/store/sessions/search/search-sessions";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { clearImportSessionInProgress } from "@/modules/cache/import-session";
import { Channel } from "pusher-js";
import { PusherEventData, PusherEventKind } from "@/domains/pusher/constants";
import { generateSessionLiveSyncUpdatePusherChannelKey } from "../sync/utils";

export class AppStoreSessionStore extends BaseSyncModelStore<SessionObservable, SessionModelData> {
  private liveSessionPusherChannels = new Map<string, Channel>();

  activeImportSessionIndexes: SessionIndexTuple[] = [];
  activeImportSessionsLiveQuerySubscription: Maybe<Subscription>;

  completedImportSessionIndexes: SessionIndexTuple[] = [];
  completedImportSessionsLiveQuerySubscription: Maybe<Subscription>;

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.Session, ...injectedDeps });
    makeObservable<
      this,
      | "liveSessionPusherChannels"
      | "subscribeActiveImportSessionsLiveQuery"
      | "unsubscribeActiveImportSessionsLiveQuery"
      | "subscribeCompletedImportSessionsLiveQuery"
      | "unsubscribeCompletedImportSessionsLiveQuery"
      | "handleSessionUpserted"
      | "handleSessionEventUpserted"
      | "handleSessionStatusUpdated"
      | "retrieveLatestInitialChatSession"
    >(this, {
      // Active Imports
      activeImportSessionsLiveQuerySubscription: observable,
      activeImportSessionIndexes: observable,
      subscribeActiveImportSessionsLiveQuery: action,
      unsubscribeActiveImportSessionsLiveQuery: action,
      activeImportSessions: computed,

      // Completed Imports
      completedImportSessionsLiveQuerySubscription: observable,
      completedImportSessionIndexes: observable,
      subscribeCompletedImportSessionsLiveQuery: action,
      unsubscribeCompletedImportSessionsLiveQuery: action,
      completedImportSessions: computed,

      updateSessionOnNewUserMessage: true,
      updateInitialSessionVariant: true,
      getPreviousChatSessions: true,
      markSessionAsDismissed: true,
      getLatestInitialChatSession: true,
      createUserMessageEvent: true,
      processSession: false,
      createSyncModel: false,
      handleSessionUpserted: true,
      handleSessionEventUpserted: true,
      handleSessionStatusUpdated: true,
      retrieveLatestInitialChatSession: true,
      liveSessionPusherChannels: observable,
      createSession: action,
      cancelImportSession: action,
      processSyncUpdate: override,
      processLiveSyncUpdate: action,
      subscribeToLiveSession: action,
      unsubscribeFromLiveSession: action,
      computeIndexes: override,
    });

    onBecomeObserved(this, "activeImportSessionIndexes", () =>
      this.subscribeActiveImportSessionsLiveQuery()
    );
    onBecomeUnobserved(this, "activeImportSessionIndexes", () =>
      this.unsubscribeActiveImportSessionsLiveQuery()
    );

    onBecomeObserved(this, "completedImportSessionIndexes", () =>
      this.subscribeCompletedImportSessionsLiveQuery()
    );
    onBecomeUnobserved(this, "completedImportSessionIndexes", () =>
      this.unsubscribeCompletedImportSessionsLiveQuery()
    );
  }

  get activeImportSessions(): SessionObservable[] {
    const activeSessionIds = this.activeImportSessionIndexes.map(
      ([_kind, _status, _created_at, model_id]) => model_id
    );
    return this.getMany(activeSessionIds);
  }

  private subscribeActiveImportSessionsLiveQuery() {
    this.activeImportSessionsLiveQuerySubscription = liveQuery(() =>
      this.localTable
        .where("[kind+status+created_at+model_id]")
        .between(
          ["IMPORT", "PROCESSING", Dexie.minKey, Dexie.minKey],
          ["IMPORT", "PROCESSING", Dexie.maxKey, Dexie.maxKey]
        )
        .keys()
    ).subscribe({
      next: ids => {
        runInAction(() => {
          this.activeImportSessionIndexes = ids as unknown as SessionIndexTuple[];
        });
      },
    });
  }

  private unsubscribeActiveImportSessionsLiveQuery() {
    this.activeImportSessionsLiveQuerySubscription?.unsubscribe();
  }

  get completedImportSessions(): SessionObservable[] {
    const ids = this.completedImportSessionIndexes.map(
      ([_kind, _status, _created_at, model_id]) => model_id
    );
    return this.getMany(ids);
  }

  private subscribeCompletedImportSessionsLiveQuery() {
    this.completedImportSessionsLiveQuerySubscription = liveQuery(() =>
      this.localTable
        .where("[kind+status+created_at+model_id]")
        .between(
          ["IMPORT", "FINISHED", Dexie.minKey, Dexie.minKey],
          ["IMPORT", "FINISHED", Dexie.maxKey, Dexie.maxKey]
        )
        .reverse()
        .keys()
    ).subscribe({
      next: ids => {
        runInAction(() => {
          this.completedImportSessionIndexes = ids as unknown as SessionIndexTuple[];
        });
      },
    });
  }

  private unsubscribeCompletedImportSessionsLiveQuery() {
    this.completedImportSessionsLiveQuerySubscription?.unsubscribe();
  }

  createSyncModel(data: SyncUpdateValue<SessionModelData>): SessionObservable {
    return new SessionObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  /**
   * Creates a new session.
   *
   * @returns The ID of the newly created session
   */
  public async createSession({
    title,
    status,
    kind,
    sessionId,
    config,
  }: {
    title?: string;
    status: SessionStatus;
    kind: SessionKind;
    sessionId?: Uuid;
    config?: SessionConfig;
    lastUserActionAt?: string;
  }): Promise<Uuid> {
    const id = sessionId || uuidModule.generate();

    // Ensure config has required schema_version if provided
    const normalizedConfig = config
      ? {
          ...config,
          schema_version: config.schema_version || ("V1" as const),
        }
      : undefined;

    await new CreateSessionOperation({
      store: this.store,
      payload: {
        id,
        title,
        status,
        kind,
        config: normalizedConfig,
      },
    }).execute();

    return id;
  }

  public async markSessionAsDismissed(sessionId: Uuid) {
    return new UpdateSessionOperation({
      store: this.store,
      payload: { id: sessionId, status: "DISMISSED" },
    }).execute();
  }

  /**
   * Cancel an import session: abort client uploads, mark dismissed, and clear local resume markers.
   */
  public async cancelImportSession(sessionId: Uuid) {
    if (!sessionId) return;

    // Abort local uploads immediately
    try {
      this.store.uploadProgress.cancelUploadsBySession(sessionId);
    } catch {
      // noop
    }

    // Mark session dismissed locally
    try {
      await this.markSessionAsDismissed(sessionId);
    } catch {
      // noop
    }

    // Clear any local in-progress resume markers so refresh won't resume/mark error
    try {
      clearImportSessionInProgress(sessionId);
    } catch {
      // noop
    }

    // Add a message to the session about the cancellation
    try {
      await this.store.sessionEvents.createMessageSentSessionEvent({
        session_id: sessionId,
        message_value: { kind: "REASONING", content: "Import canceled" },
      });
    } catch {
      // noop
    }

    // TODO: add a backend session cancellation endpoint, like so:
    // await api.post(`/v2/sessions/{session_id}/cancel`, {
    //   params: { path: { session_id: sessionId } },
    // });
  }

  /**
   * Updates a session's title
   */
  public async updateSessionOnNewUserMessage(
    sessionId: Uuid,
    {
      title,
      status,
      lastUserActionAt,
    }: {
      title?: string;
      status?: SessionStatus;
      lastUserActionAt?: string;
    }
  ) {
    return new UpdateSessionOperation({
      store: this.store,
      payload: {
        id: sessionId,
        title,
        status,
        last_user_action_at: lastUserActionAt,
      },
    }).execute();
  }

  public async updateInitialSessionVariant(variant: SessionConfigVariant) {
    const session = await this.retrieveLatestInitialChatSession();

    if (!session) {
      logger.warn({
        message: "Failed to update initial session variant: no initial session found",
      });

      return;
    }

    await new UpdateSessionOperation({
      store: this.store,
      payload: {
        id: session.model_id,
        status: session.model_data.status,
        title: session.model_data.title,
        kind: session.model_data.kind,
        config: {
          schema_version: session.model_data.config?.schema_version ?? "V1",
          enable_agent_digests: session.model_data.config?.enable_agent_digests ?? null,
          variant,
        },
      },
    }).execute();
  }

  /**
   * Gets the latest initial chat session that can be used as a placeholder.
   * Returns a session with status INITIAL and kind GENERIC or MEM_IT.
   * If none exists, creates a new one.
   */
  public async getLatestInitialChatSession(): Promise<Uuid> {
    const latestSession = await this.retrieveLatestInitialChatSession();
    if (latestSession) {
      return latestSession.model_id;
    }

    // No initial session exists, create a new one
    const sessionId = uuidModule.generate();
    return await this.createSession({
      sessionId,
      title: "Mem It Session",
      status: "INITIAL",
      kind: "GENERIC",
      config: {
        schema_version: "V2",
        variant: "STANDARD",
        enable_agent_digests: true,
      },
    });
  }

  private retrieveLatestInitialChatSession =
    async (): Promise<SyncUpdateValue<SessionModelData> | null> => {
      const latestSession = await this.localTable
        .where("[kind+status+created_at+model_id]")
        .between(
          ["GENERIC", "INITIAL", Dexie.minKey, Dexie.minKey],
          ["GENERIC", "INITIAL", Dexie.maxKey, Dexie.maxKey]
        )
        .last();

      return latestSession ?? null;
    };

  /**
   * Creates a user message event for a session
   */
  public async createUserMessageEvent({
    sessionId,
    content,
    context,
  }: {
    sessionId: Uuid;
    content: string;
    context?: Record<string, unknown>;
  }): Promise<void> {
    await this.store.sessionEvents.createSessionEvent({
      session_id: sessionId,
      event_kind: "USER_ACTION_V1",
      event_value: {
        kind: "USER_ACTION_V1",
        parts: [
          {
            kind: "MARKDOWN_V1",
            id: uuidModule.generate(),
            content,
          },
        ],
        context: context || null,
      },
    });
  }

  /**
   * Process/retry a session by setting status to processing and calling the process endpoint
   */
  public async processSession(sessionId: Uuid): Promise<void> {
    if (!sessionId) {
      logger.warn({
        message: "Cannot process session: no session ID provided",
      });

      return;
    }

    try {
      // First update the session status to PROCESSING
      await this.updateSessionOnNewUserMessage(sessionId, {
        status: "PROCESSING",
      });

      // Then call the process session API endpoint
      await api.post(`/v2/sessions/{session_id}/process`, {
        params: { path: { session_id: sessionId } },
        body: {
          immediate: true,
        },
      });
    } catch (error) {
      logger.error({
        message: "Failed to process session",
        info: {
          sessionId,
          error: objectModule.safeErrorAsJson(error as Error),
        },
      });
    }
  }

  public getPreviousChatSessions = (): Promise<SessionPreviousChats[]> => {
    return Dexie.waitFor(searchPreviousChatSessions({ store: this.store }));
  };

  public computeIndexes({
    store,
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<SessionModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<SessionModelData>[];
  }): Record<string, unknown> {
    return new SessionIndexes({ store, remoteData, optimisticUpdates }).indexes;
  }

  public async processSyncUpdate(update: SyncUpdate<SessionModelData>) {
    await super.processSyncUpdate(update);

    if (update.kind === "UPSERTED" || update.kind === "ACL_UPSERTED") {
      this.subscribeToLiveSession(update.value.model_id);
      return;
    }

    if (update.kind === "DELETED" || update.kind === "ACL_REVOKED") {
      this.unsubscribeFromLiveSession(update.value.model_id);
      return;
    }
  }

  public subscribeToLiveSession(sessionId: string) {
    if (this.liveSessionPusherChannels.has(sessionId)) return;
    const sessionPusherChannelKey = generateSessionLiveSyncUpdatePusherChannelKey({ sessionId });
    const channel = this.pusher.subscribe(sessionPusherChannelKey);

    channel.bind(PusherEventKind.SESSION_UPSERTED, this.handleSessionUpserted);
    channel.bind(PusherEventKind.SESSION_EVENT_UPSERTED, this.handleSessionEventUpserted);
    channel.bind(PusherEventKind.SESSION_STATUS_UPDATED, this.handleSessionStatusUpdated);

    this.liveSessionPusherChannels.set(sessionId, channel);
  }

  public unsubscribeFromLiveSession(sessionId: string) {
    const channel = this.liveSessionPusherChannels.get(sessionId);
    if (channel) {
      this.pusher.unsubscribe(channel.name);
    }

    this.liveSessionPusherChannels.delete(sessionId);
  }

  async processLiveSyncUpdate(
    _sync_operation_id: string,
    value: SessionUpsertedSyncUpdateValue
  ): Promise<void> {
    // Remove any existing optimistic updates for this session
    await this.store.sync.actionQueue.removeAllOptimisticUpdatesByModelId(value.model_id);
    await this.processSyncUpdate({ kind: "UPSERTED", value });
  }

  private handleSessionUpserted = async ({
    value,
    sync_operation_id,
  }: PusherEventData<PusherEventKind.SESSION_UPSERTED>) => {
    await this.processLiveSyncUpdate(sync_operation_id, value);
  };

  private handleSessionEventUpserted = async ({
    value,
    sync_operation_id,
  }: PusherEventData<PusherEventKind.SESSION_EVENT_UPSERTED>) => {
    await this.store.sessionEvents.processLiveSyncUpdate(sync_operation_id, value);
  };

  private handleSessionStatusUpdated = async ({
    session_id,
    status,
  }: PusherEventData<PusherEventKind.SESSION_STATUS_UPDATED>) => {
    new UpdateSessionOperation({
      store: this.store,
      payload: {
        id: session_id,
        status,
      },
    }).execute();
  };
}
